import Sheet from "components/sheet/Sheet";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Input from "components/form/input/Input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SelectCustomer from "elements/register/SelectCustomer";
import { useAppSelector } from "hooks/redux";
import { ICustomer } from "redux/reducers/models/customer";
import { ICart } from "redux/reducers/models/cart";
import { Badge } from "components/badge/Badge";
import classNames from "classnames";
import "pages/secure/facility/register/registerNew/cartAudits/cartAudits.scss";

/** Matches TableServiceCart seat-action locks for guest name / customer edits. */
export function isSeatEditableForGuestDetails(cart: ICart | null | undefined): boolean {
  if (!cart) {
    return false;
  }

  // Same gate as TableServiceCart handleOpenCartDetailsSheet / Add Guest Name action
  return cart.status === "open" && cart.order?.financial_status !== "partially_paid";
}

interface IItemActionsState {
  cartDescription: string;
  selectedCustomer: ICustomer | null;
}

interface IEditSeatDetailsProps {
  editSeatVisible: boolean;
  onCancel?: () => void;
  onOk?: (priceChangeInfo: IItemActionsState) => void;
  onOkMultiSeat?: (seats: Record<number, IItemActionsState>) => void;
  /**The cart's current customer and description: Object {customer: CustomerObject; description: string} */
  prevCartDetails: {
    customer: ICustomer | null;
    description: string;
  };
  /** When provided (e.g. from table selection), show seat badges to pick which seat to update */
  tableServiceCarts?: Array<ICart>;
  selectedSeatId?: number;
  onSeatChange?: (cart: ICart) => void;
}

function getCartSeatDetails(cart: ICart): IItemActionsState {
  return {
    selectedCustomer: cart.customer ?? null,
    cartDescription: cart.description ?? "",
  };
}


function EditSeatDetails(props: IEditSeatDetailsProps) {
  const { t } = useTranslation();
  const { cartStore } = useAppSelector(store => store);
  const [itemActionsState, setItemActionsState] = useState<IItemActionsState>({
    cartDescription: "",
    selectedCustomer: null,
  });
  // Remount SelectCustomer so search text clears when switching seats
  const [customerSearchKey, setCustomerSearchKey] = useState(0);
  const activeSeatIdRef = useRef<number | undefined>(undefined);

  const selectedSeat =
    props.tableServiceCarts?.find(cart => cart.id === props.selectedSeatId) ?? cartStore?.cart;
  const selectedSeatEditable = isSeatEditableForGuestDetails(selectedSeat);
  const [guestSeatDraftsByCartId, setGuestSeatDraftsByCartId] = useState<Record<number, IItemActionsState>>({});

  function persistSeatDraft(seatId: number | undefined, nextState: IItemActionsState) {
    if (seatId == null) {
      return;
    }
    setGuestSeatDraftsByCartId(prev => ({...prev, [seatId]: {...nextState}}));
  }

  function persistActiveSeatDraft(nextState: IItemActionsState) {
    persistSeatDraft(activeSeatIdRef.current ?? props.selectedSeatId, nextState);
  }

  function loadFormForSeat(seatId: number, fallback: IItemActionsState) {
    activeSeatIdRef.current = seatId;
    setItemActionsState(guestSeatDraftsByCartId[seatId] ?? fallback);
  }

  // Hydrate on open from draft (if any) or server seat details. Drafts are intentionally kept on close.
  useEffect(() => {
    if (!props.editSeatVisible) {
      setGuestSeatDraftsByCartId({});
      return;
    }

    const seatId = props.selectedSeatId;
    if (seatId != null) {
      loadFormForSeat(seatId, {
        selectedCustomer: props.prevCartDetails?.customer ?? null,
        cartDescription: props.prevCartDetails?.description ?? "",
      });
    } else {
      setItemActionsState({
        selectedCustomer: props.prevCartDetails?.customer ?? null,
        cartDescription: props.prevCartDetails?.description ?? "",
      });
    }
  }, [props.editSeatVisible]);

  function onOk() {
    if (!selectedSeatEditable) {
      return;
    }
    // Snapshot before save so an accidental close after Update still has the selection if the request fails
    persistActiveSeatDraft(itemActionsState);
    if (props.onOkMultiSeat) {
      void props?.onOkMultiSeat({...guestSeatDraftsByCartId});
      return;
    }
    void props.onOk?.({ ...itemActionsState });
  }

  function onCancel() {
    persistActiveSeatDraft(itemActionsState);
    props.onCancel?.();
  }

  function handleSeatBadgeClick(cart: ICart) {
    const currentSeatId = activeSeatIdRef.current ?? props.selectedSeatId;
    persistSeatDraft(currentSeatId, itemActionsState);

    // Restore this seat's draft if any; otherwise cart defaults (empty search when none)
    loadFormForSeat(cart.id, getCartSeatDetails(cart));
    setCustomerSearchKey(key => key + 1);
    props.onSeatChange?.(cart);
  }

  function handleLineItemInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setItemActionsState(prevState => {
      const nextState = { ...prevState, [id]: value };
      persistActiveSeatDraft(nextState);
      return nextState;
    });
  }

  function handleRemoveCustomer() {
    setItemActionsState(prevState => {
      const nextState = {
        ...prevState,
        selectedCustomer: null,
      };
      persistActiveSeatDraft(nextState);
      return nextState;
    });
  }

  function handleCustomerSelection(customer: ICustomer) {
    setItemActionsState(prevState => {
      const nextState = {
        ...prevState,
        selectedCustomer: customer,
      };
      persistActiveSeatDraft(nextState);
      return nextState;
    });
  }

  return (
    <Sheet
      open={props.editSeatVisible}
      size="small"
      closable
      title={t("Details")}
      onCancel={onCancel}
      onOk={onOk}
      cancelText={t("Cancel")}
      okText={t("Update")}
      okDisabled={!cartStore?.cart || !selectedSeatEditable}
      overflow
    >
      {props.tableServiceCarts?.length > 0 ? (
        <div className="mb-4">
          <p className="ui-cart-audit-secondary pb-1">{t("Assign guest name to:")}</p>
          <div className="flex items-center gap-2 overflow-x-auto ui-cart-audit-scroll-on-hover">
            {props.tableServiceCarts.map((cart, index) => {
              const seatLocked = !isSeatEditableForGuestDetails(cart);
              return (
                <Badge
                  key={cart.id}
                  onClick={seatLocked ? undefined : () => handleSeatBadgeClick(cart)}
                  className={classNames({
                    "cursor-pointer": !seatLocked,
                    "cursor-not-allowed": seatLocked,
                  })}
                  outline={!seatLocked}
                  size="small"
                  type={props.selectedSeatId === cart.id ? "primary" : "gray"}
                  colour={seatLocked ? "#6B7280" : undefined}
                  backgroundColour={seatLocked ? "#D1D5DB" : undefined}
                >
                  {t("Seat")} {index + 1}
                </Badge>
              );
            })}
          </div>
        </div>
      ) : null}
      <Input
        placeholder={t("Guest name...")}
        label={t("Guest Name or Description")}
        id="cartDescription"
        value={itemActionsState.cartDescription}
        onChange={handleLineItemInputChange}
      />
      {itemActionsState.selectedCustomer ? (
        <div className="mt-4">
          <p className="mb-2 font-medium">{t("Customer")}</p>
          <div className="flex flex-row justify-start">
            <div className="mr-3 mt-2">
              <FontAwesomeIcon icon={["far", "user"]} style={{ fontSize: "24px", verticalAlign: "middle" }} />
            </div>
            <div>
              <p>{itemActionsState.selectedCustomer?.full_name}</p>
              <p className="text-subdued">{itemActionsState.selectedCustomer?.email}</p>
            </div>
            <div className="ml-auto">
              <a style={{ marginRight: "0.8rem" }} className="cursor-pointer" onClick={handleRemoveCustomer}>
                {t("Remove")}
              </a>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <SelectCustomer
            key={`${props.selectedSeatId ?? "no-seat"}-${customerSearchKey}`}
            onCustomerChange={customer => handleCustomerSelection(customer)}
            permissionLevel={"facility"}
            newCustomer
            label={t("Customer")}
            placeholder={t("Search for a customer...")}
            usePortal
            clearOnSelection
          />
        </div>
      )}
    </Sheet>
  );
}

export default EditSeatDetails;
